import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectSettings } from '../../../store/selectors/databaseSelectors';
import { updateSetting } from '../../../store/slices/databaseSlice';
import { settingsService } from '../../../services';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG, SETTING_TYPES } from '../../../constants';
import toast from 'react-hot-toast';
import { ConfirmationModal, ConfirmationType } from '../../../components/shared/ui/ConfirmationModal';

interface ClientLogoEntry {
  clientName: string;
  logoName: string;
}

export const ClientLogosManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectSettings);

  const [clientLogos, setClientLogos] = useState<Record<string, string>>({});
  const [newClientName, setNewClientName] = useState('');
  const [newLogoName, setNewLogoName] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editClientName, setEditClientName] = useState('');
  const [editLogoName, setEditLogoName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);

  useEffect(() => {
    const clientLogosSetting = settings.find(s => s.type === SETTING_TYPES.CLIENT_LOGOS);
    if (clientLogosSetting) {
      setSettingId(clientLogosSetting.id);
      const value = clientLogosSetting.value;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        setClientLogos(value as Record<string, string>);
      }
    }
  }, [settings]);

  const saveClientLogos = async (newLogos: Record<string, string>) => {
    setIsSubmitting(true);
    try {
      let updatedSetting;
      if (settingId) {
        try {
          updatedSetting = await settingsService.update(settingId, { value: newLogos });
        } catch {
          // Setting might not exist, try to create it
          updatedSetting = await settingsService.create({
            type: SETTING_TYPES.CLIENT_LOGOS,
            value: newLogos,
            description: 'Mapping of client names to logo identifiers',
          });
        }
      } else {
        // No settingId, create new setting
        updatedSetting = await settingsService.create({
          type: SETTING_TYPES.CLIENT_LOGOS,
          value: newLogos,
          description: 'Mapping of client names to logo identifiers',
        });
      }
      dispatch(updateSetting(updatedSetting));
      setSettingId(updatedSetting.id);
      setClientLogos(newLogos);
      return true;
    } catch (error) {
      console.error('Error saving client logos:', error);
      toast.error('Failed to save client logos. Please try again.', TOAST_CONFIG.toastOptions.error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientName.trim() || !newLogoName.trim()) {
      toast('Please enter both client name and logo name', TOAST_WARNING_CONFIG);
      return;
    }

    if (clientLogos[newClientName.trim()]) {
      toast('Client name already exists. Edit the existing entry instead.', TOAST_WARNING_CONFIG);
      return;
    }

    const newLogos = { ...clientLogos, [newClientName.trim()]: newLogoName.trim() };
    const success = await saveClientLogos(newLogos);
    if (success) {
      toast.success('Client logo mapping added successfully!', TOAST_CONFIG.toastOptions.success);
      setNewClientName('');
      setNewLogoName('');
    }
  };

  const handleEdit = (clientName: string) => {
    setEditingKey(clientName);
    setEditClientName(clientName);
    setEditLogoName(clientLogos[clientName]);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditClientName('');
    setEditLogoName('');
  };

  const handleSaveEdit = async () => {
    if (!editClientName.trim() || !editLogoName.trim()) {
      toast('Please enter both client name and logo name', TOAST_WARNING_CONFIG);
      return;
    }

    const newLogos = { ...clientLogos };

    if (editingKey !== editClientName.trim()) {
      if (newLogos[editClientName.trim()] && editClientName.trim() !== editingKey) {
        toast('Client name already exists.', TOAST_WARNING_CONFIG);
        return;
      }
      delete newLogos[editingKey!];
    }

    newLogos[editClientName.trim()] = editLogoName.trim();

    const success = await saveClientLogos(newLogos);
    if (success) {
      toast.success('Client logo mapping updated successfully!', TOAST_CONFIG.toastOptions.success);
      handleCancelEdit();
    }
  };

  const openDeleteModal = (clientName: string) => {
    setClientToDelete(clientName);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setClientToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;

    const newLogos = { ...clientLogos };
    delete newLogos[clientToDelete];

    const success = await saveClientLogos(newLogos);
    if (success) {
      toast.success('Client logo mapping deleted successfully!', TOAST_CONFIG.toastOptions.success);
    }
    closeDeleteModal();
  };

  const entries: ClientLogoEntry[] = Object.entries(clientLogos)
    .map(([clientName, logoName]) => ({ clientName, logoName }))
    .sort((a, b) => a.clientName.localeCompare(b.clientName));

  return (
    <div className="p-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New Client Logo Mapping
          </h3>

          <form onSubmit={handleAddNew} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Client Name *
              </label>
              <input
                type="text"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="e.g., SunCore, Elav8"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Logo Name *
              </label>
              <input
                type="text"
                value={newLogoName}
                onChange={(e) => setNewLogoName(e.target.value)}
                placeholder="e.g., SUNCORE-ASCND-BL ELECTRIC-SOLARQUOTE"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !newClientName.trim() || !newLogoName.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Adding...' : 'Add Client Logo Mapping'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Client Logo Mappings
            </h3>
            <span className="text-xs text-slate-500">
              {entries.length} mappings
            </span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {entries.length > 0 ? (
              <div>
                {/* Table Header */}
                <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-200 bg-slate-100 rounded-t-lg">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Client Name</div>
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Client Logo</div>
                  </div>
                  <div className="w-16"></div>
                </div>
                {/* Table Rows */}
                {entries.map((entry) => {
                  const isEditing = editingKey === entry.clientName;

                  return (
                    <div
                      key={entry.clientName}
                      className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border-b border-x border-slate-200 last:rounded-b-lg"
                    >
                      {isEditing ? (
                        <>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={editClientName}
                              onChange={(e) => setEditClientName(e.target.value)}
                              className="px-2 py-0.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Client Name"
                              disabled={isSubmitting}
                            />
                            <input
                              type="text"
                              value={editLogoName}
                              onChange={(e) => setEditLogoName(e.target.value)}
                              className="px-2 py-0.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Logo Name"
                              disabled={isSubmitting}
                            />
                          </div>
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSubmitting}
                            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Save"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                            className="p-1 text-slate-500 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div className="text-xs font-medium text-slate-900 truncate" title={entry.clientName}>
                              {entry.clientName}
                            </div>
                            <div className="text-xs text-slate-600 truncate" title={entry.logoName}>
                              {entry.logoName}
                            </div>
                          </div>
                          <button
                            onClick={() => handleEdit(entry.clientName)}
                            disabled={isSubmitting || editingKey !== null}
                            className="p-1 text-slate-500 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                            title="Edit"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openDeleteModal(entry.clientName)}
                            disabled={isSubmitting || editingKey !== null}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-500 text-sm">No client logo mappings configured yet</p>
                <p className="text-slate-400 text-xs mt-1">Add your first mapping using the form or run migrations</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Client Logo Mapping"
        message={`Are you sure you want to delete the mapping for "${clientToDelete}"? This action cannot be undone.`}
        type={ConfirmationType.DANGER}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};
